import React from 'react';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface AnimatedCollapseProps {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  /** Duration in ms (default: 250) */
  duration?: number;
}

/**
 * GPU-friendly collapse animation using CSS grid-template-rows.
 *
 * Unlike Framer Motion height animations, this avoids:
 * - Forced reflows from JS measuring content height
 * - Non-composited `height` property animations
 * - Layout thrashing during expand/collapse
 *
 * Content stays in DOM when collapsed (overflow: hidden, min-height: 0).
 * Respects prefers-reduced-motion — transitions are instant when enabled.
 */
export const AnimatedCollapse: React.FC<AnimatedCollapseProps> = ({
  open,
  children,
  className,
  duration = 250,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const effectiveDuration = prefersReducedMotion ? 0 : duration;
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const TABINDEX_DATA_KEY = 'data-collapse-prev-tabindex';
    const DISABLED_DATA_KEY = 'data-collapse-prev-disabled';
    const focusableSelector = [
      'a[href]',
      'area[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[tabindex]',
      '[contenteditable="true"]',
      '[role="button"]',
      '[role="switch"]',
      '[role="radio"]',
      '[role="checkbox"]',
    ].join(',');

    const restoreFocusableState = () => {
      content.querySelectorAll<HTMLElement>(`[${TABINDEX_DATA_KEY}], [${DISABLED_DATA_KEY}]`).forEach((el) => {
        if (el.hasAttribute(TABINDEX_DATA_KEY)) {
          const prev = el.getAttribute(TABINDEX_DATA_KEY);
          if (prev === '__none__') {
            el.removeAttribute('tabindex');
          } else if (prev != null) {
            el.setAttribute('tabindex', prev);
          }
          el.removeAttribute(TABINDEX_DATA_KEY);
        }

        if (el.hasAttribute(DISABLED_DATA_KEY) && 'disabled' in el) {
          const prevDisabled = el.getAttribute(DISABLED_DATA_KEY);
          (el as HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled = prevDisabled === 'true';
          el.removeAttribute(DISABLED_DATA_KEY);
        }
      });
    };

    const disableFocusableState = () => {
      content.querySelectorAll<HTMLElement>(focusableSelector).forEach((el) => {
        if (!el.hasAttribute(TABINDEX_DATA_KEY)) {
          const prevTabIndex = el.getAttribute('tabindex');
          el.setAttribute(TABINDEX_DATA_KEY, prevTabIndex ?? '__none__');
        }
        el.setAttribute('tabindex', '-1');

        if ('disabled' in el && !el.hasAttribute(DISABLED_DATA_KEY)) {
          const control = el as HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          el.setAttribute(DISABLED_DATA_KEY, control.disabled ? 'true' : 'false');
          control.disabled = true;
        }
      });
    };

    const activeEl = document.activeElement;
    if (!open && activeEl instanceof HTMLElement && content.contains(activeEl)) {
      activeEl.blur();
    }

    if (open) {
      restoreFocusableState();
      content.removeAttribute('inert');
      return;
    }

    // Keep closed content in the DOM for animation continuity, but remove it
    // from keyboard/screen-reader interaction to avoid hidden-focus conflicts.
    disableFocusableState();
    content.setAttribute('inert', '');

    return () => {
      restoreFocusableState();
      content.removeAttribute('inert');
    };
  }, [open]);

  return (
    <div
      className={cn('grid w-full', className)}
      style={{
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        // Inline margin:0 when closed — reliably overrides parent space-y/gap rules
        // (Tailwind v4 !m-0 class doesn't consistently override space-y selectors)
        ...(!open && { margin: 0 }),
        transitionProperty: 'grid-template-rows, opacity, margin',
        transitionDuration: `${effectiveDuration}ms`,
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: open ? 'auto' : 'none',
      }}
      aria-hidden={!open}
    >
      <div ref={contentRef} className="overflow-hidden min-h-0">
        {children}
      </div>
    </div>
  );
};
